import { Button, Icon, Input } from '@southfields-digital/mpxlive-components';
import { useState, useEffect } from 'react';

import Select from 'src/components/Select';
import { Member, MemberTypeOptions, MemberTypes } from 'src/types/matchControl/member';

type Props = {
  member: Member;
  handleUpdateMember: (member: Member) => void;
  handleDeleteMember: () => void;
};

const MemberRow = ({ member, handleUpdateMember, handleDeleteMember }: Props) => {
  const [localMember, setLocalMember] = useState(member);
  const [numberDisplayValue, setNumberDisplayValue] = useState(member.number?.toString() || '');

  useEffect(() => {
    setLocalMember(member);
    setNumberDisplayValue(member.number?.toString() || '');
  }, [member]);

  const updateMember = (updatedMember: Member) => {
    setLocalMember(updatedMember);
    handleUpdateMember(updatedMember);
  };

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedMember = { ...localMember, name: e.target.value };
    updateMember(updatedMember);
  };

  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedMember = { ...localMember, number: e.target.value };
    updateMember(updatedMember);
  };

  const handleChangeType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const updatedMember = { ...localMember, type: e.target.value as MemberTypes };
    updateMember(updatedMember);
  };

  const handleChangeGoals = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
    const updatedMember = { ...localMember, goals: value };
    updateMember(updatedMember);
  };

  const handleChangeCaps = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
    const updatedMember = { ...localMember, caps: value };
    updateMember(updatedMember);
  };

  const handleChangeMatches = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
    const updatedMember = { ...localMember, matches: value };
    updateMember(updatedMember);
  };

  const handleChangeAge = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
    const updatedMember = { ...localMember, age: value };
    updateMember(updatedMember);
  };

  const handleNumberBlur = () => {
    // If the field is empty when user leaves it, reset to the current member number
    if (numberDisplayValue === '') {
      setNumberDisplayValue(localMember.number?.toString() || '');
    }
  };

  return (
    <div className="flex flex-row gap-2 items-center px-2 py-1">
      <Icon icon="sort" weight="bold" className="text-[#5B5D70]" />
      <Input
        type="text"
        value={numberDisplayValue}
        onChange={handleChangeNumber}
        onBlur={handleNumberBlur}
        placeholder="Member Number"
        className="flex-1"
      />
      <Input
        value={localMember.name}
        onChange={handleChangeName}
        placeholder="Member Name"
        className="flex-[2.5]"
      />
      <Select
        id="type"
        value={localMember.type}
        options={MemberTypeOptions}
        onChange={handleChangeType}
        className="flex-1"
      />
      <Input
        type="number"
        value={localMember.goals?.toString() || ''}
        onChange={handleChangeGoals}
        placeholder="Goals"
        min={0}
        className="flex-1"
      />
      <Input
        type="number"
        value={localMember.caps?.toString() || ''}
        onChange={handleChangeCaps}
        placeholder="Caps"
        min={0}
        className="flex-1"
      />
      <Input
        type="number"
        value={localMember.matches?.toString() || ''}
        onChange={handleChangeMatches}
        placeholder="Matches"
        min={0}
        className="flex-1"
      />
      <Input
        type="number"
        value={localMember.age?.toString() || ''}
        onChange={handleChangeAge}
        placeholder="Age"
        min={0}
        max={99}
        className="flex-1"
      />
      <Button variant="danger" onClick={handleDeleteMember}>
        <Icon icon="trash" />
      </Button>
    </div>
  );
};

export default MemberRow;
